import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import { applyZIndexAboveOpenModals } from '../src/shared/helpers/modal_stacking'

export default class extends Controller {
  static values = {
    ajaxUrl: String,
    placeholder: String,
    noResults: String,
    multiple: { type: Boolean, default: false },
    preload: { type: Boolean, default: false },
    preloadAll: { type: Boolean, default: false },
    optionMethodName: { type: String, default: 'full_name' },
    clearButton: { type: Boolean, default: false },
    clearButtonTitle: { type: String, default: 'Clear All' }
  }

  connect () {
    this.modal = this.element.closest('.modal')

    if (this.modal) {
      this.onModalShown = () => this.initializeTomSelect()
      this.modal.addEventListener('shown.bs.modal', this.onModalShown)

      if (this.modal.classList.contains('show')) {
        this.initializeTomSelect()
      }
      return
    }

    this.initializeTomSelect()
  }

  disconnect () {
    this.destroyTomSelect()

    if (this.onModalShown) {
      this.modal?.removeEventListener('shown.bs.modal', this.onModalShown)
    }
  }

  initializeTomSelect () {
    this.destroyTomSelect()

    const settings = {
      placeholder: this.placeholderValue,
      closeAfterSelect: true,
      plugins: this.#buildPlugins(),
      render: {
        no_results: () => {
          return `<div class="no-results">${this.noResultsValue}</div>`
        },
        ...this.#iconRenderers()
      }
    }

    if (this.hasAjaxUrlValue) {
      settings.valueField = 'id'
      settings.labelField = 'text'
      settings.searchField = ['text', 'searchText']
      settings.load = this.loadOptions.bind(this)
    }

    // +preloadAll+ lifts Tom Select's option-render cap (50 by default) so a long
    // static list already in the DOM — the 134 spoken languages — is browsable
    // without typing. +preload+ is a different thing: it fetches an ajax list once.
    if (this.preloadAllValue) settings.maxOptions = null

    if (this.modal) {
      // Portal to body so .modal-body overflow does not clip the menu; raise z-index
      // on open so nested modals (stackAboveParentModal) do not cover it.
      settings.dropdownParent = 'body'
      settings.onDropdownOpen = (dropdown) => {
        applyZIndexAboveOpenModals(dropdown)
      }
    }

    this.tomSelect = new TomSelect(this.element, settings)

    if (this.preloadValue && this.hasAjaxUrlValue) {
      this.preloadOptions()
    }

    this.element.dispatchEvent(new CustomEvent('tom-select:ready', { bubbles: true }))
  }

  destroyTomSelect () {
    const instance = this.tomSelect || this.element.tomselect
    instance?.destroy()
    this.tomSelect = null
  }

  async loadOptions (query, callback) {
    if (!query.length || query.length < 2) return callback()

    try {
      const url = new URL(this.ajaxUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url.toString())
      const json = await response.json()

      callback(json.data.map(item => ({
        id: item.id,
        text: item.attributes[this.optionMethodNameValue],
        searchText: this.buildSearchText(item),
        data: item
      })))
    } catch (err) {
      callback()
    }
  }

  async preloadOptions () {
    try {
      const response = await fetch(this.ajaxUrlValue)
      const json = await response.json()

      const options = json.data.map(item => ({
        id: item.id,
        text: item.attributes[this.optionMethodNameValue],
        searchText: this.buildSearchText(item),
        data: item
      }))

      this.tomSelect.addOptions(options)
    } catch (err) {
      console.error('Failed to preload options:', err)
    }
  }

  buildSearchText (item) {
    const label = item.attributes[this.optionMethodNameValue] || ''
    const phone = item.attributes.phone || ''
    const compactPhone = String(phone).replace(/\D/g, '')

    return [label, phone, compactPhone].filter(Boolean).join(' ')
  }

  #iconRenderers () {
    if (!this.element.querySelector('option[data-icon]')) return {}

    const render = (data, escape) => this.#renderOption(data, escape)

    return { option: render, item: render }
  }

  #renderOption (data, escape) {
    const label = escape(data.text ?? '')
    if (!data.icon) return `<div>${label}</div>`

    return `<div class="ts-option-with-icon"><i class="${escape(data.icon)}" aria-hidden="true"></i><span>${label}</span></div>`
  }

  #buildPlugins () {
    const plugins = { change_listener: {} }

    if (this.multipleValue) {
      plugins.remove_button = {}
    }

    if (this.clearButtonValue) {
      plugins.clear_button = {
        title: this.clearButtonTitleValue
      }
    }

    return plugins
  }
}
